import { useState } from 'react'
import { Check, Shield, AlertCircle, Pencil } from 'lucide-react'
import { formatDate } from '../utils/dateUtils'
import { useI18n } from '../i18n'
import { secondActiveIndex } from '../utils/insuranceDiscovery'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * InsuranceCandidateList — shared discovery pick-list + COB secondary flow.
 *
 * D (v242.7.0): the single confirm/pick surface reused by both discovery hosts:
 *   • HomeScreen  → wrapped in a bottom sheet (InsuranceCandidateSheet), opened
 *                   from the shrunk DiscoveryToast nudge.
 *   • Onboarding  → rendered inline inside the insurance step's discovery phase.
 *
 * Purely presentational + local flow state. All persistence is delegated to the
 * host via index-based callbacks so each surface can wire its own store:
 *   - HomeScreen : applyDiscoveryResult / applyDiscoverySecondary / dismissDiscoveryResult
 *   - Onboarding : applyCandidateAt / applySecondaryAt / finishDiscovery (onDone) / skipDiscovery
 *                  (P12, INSURE-2b: onboarding offers the second plan through the same COB)
 *
 * The COB branch mirrors the proven v238.11.0 logic previously embedded in
 * DiscoveryToast: after a primary is applied, if another *active* candidate with a
 * member ID exists (utils/insuranceDiscovery.js secondActiveIndex) and onApplySecondary
 * is provided, offer to add it as secondary. onDone fires on every completing path and
 * never after a failed apply.
 *
 * Candidate shape (from rcm_service _parse_discovery_response):
 *   { payerName, payerId, memberIdGuess, carrierId, confidence: 'high'|'medium'|'low',
 *     active: bool|null, subscriberFirstName, subscriberLastName, subscriberDob,
 *     groupNumber, planType }
 *
 * Props:
 *   candidates      — array (already sorted active-first by the API)
 *   variant         — 'sheet' | 'inline'  (density; default 'sheet')
 *   singleConfirm   — true when discovery status === 'found' (one candidate,
 *                     pre-selected, no radios, green confirm CTA)
 *   onApplyPrimary  — async (index) => { success }   REQUIRED
 *   onApplySecondary— async (index) => { success }   optional (enables COB)
 *   onSkip          — () => void   "None of these / not mine → manual card"
 *   onDone          — (didSecondary:boolean) => void  fired after apply completes
 */

const NAVY  = '#15354F'
const BLUE  = '#46A8F9'
const LIGHT = '#A7D9FC'
const ICE   = '#E9F8FF'
const GREEN = '#66D170'
const SLATE = '#94A3B8'
const GREEN_INK = '#2F9E4A'
const SLATE_INK = '#64748B'

// Confidence → badge palette. High=green, likely=blue, low=slate. (No amber:
// the discovery pick-list is not an "attention-required" clinical surface.)
// PROD-305: every label in this file goes through t() (en + es); the helpers take it.
function confBadge(conf, t) {
  const c = String(conf || 'low').toLowerCase()
  if (c === 'high')   return { label: t('insuranceCandidates.matchHigh', 'High match'),     bg: 'rgba(102,209,112,0.16)', fg: GREEN_INK }
  if (c === 'medium') return { label: t('insuranceCandidates.matchLikely', 'Likely match'), bg: 'rgba(70,168,249,0.14)',  fg: BLUE }
  return { label: t('insuranceCandidates.matchLow', 'Low match'), bg: '#F1F5F9', fg: SLATE_INK }
}

// Masked member id — show last 4 only (PHI-conscious, matches card-capture).
function maskMember(id) {
  const s = String(id || '').trim()
  if (!s) return null
  if (s.length <= 4) return s
  return `•••• ${s.slice(-4)}`
}

function memberLine(cand, t) {
  const bits = []
  const m = maskMember(cand.memberIdGuess)
  if (m) bits.push(t('insuranceCandidates.memberLast4', { defaultValue: 'Member {{id}}', id: m }))
  if (cand.planType) bits.push(String(cand.planType).toUpperCase())
  if (cand.active === false && cand.terminationDate) bits.push(t('insuranceCandidates.endedOn', { defaultValue: 'ended {{date}}', date: cand.terminationDate }))
  return bits.join(' · ')
}

function subscriberName(cand) {
  const f = (cand.subscriberFirstName || '').trim()
  const l = (cand.subscriberLastName || '').trim()
  return [f, l].filter(Boolean).join(' ') || null
}

// State chip (icon + border color) — green active / navy ended / slate unknown.
function stateChip(cand, size) {
  const px = size || 38
  const sq = { width: px, height: px, borderRadius: px >= 38 ? 11 : 10,
               display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }
  if (cand.active === true) {
    return (
      <span style={{ ...sq, background: 'rgba(102,209,112,0.14)', border: `1.5px solid ${GREEN}` }}>
        <Check size={18} strokeWidth={2.6} style={{ color: GREEN_INK }} />
      </span>
    )
  }
  if (cand.active === false) {
    return (
      <span style={{ ...sq, background: ICE, border: `1.5px solid ${NAVY}` }}>
        <AlertCircle size={18} style={{ color: NAVY }} />
      </span>
    )
  }
  return (
    <span style={{ ...sq, background: 'rgba(70,168,249,0.12)', border: `1.5px solid ${LIGHT}` }}>
      <Shield size={18} style={{ color: BLUE }} />
    </span>
  )
}

function statusPill(cand, t) {
  if (cand.active === true)  return { text: t('insuranceCandidates.statusActive', 'Active'), bg: 'rgba(102,209,112,0.16)', fg: GREEN_INK }
  if (cand.active === false) return { text: t('eligibility.planEnded', 'Plan ended'), bg: ICE, fg: NAVY }
  return null
}

export default function InsuranceCandidateList({
  candidates = [],
  variant = 'sheet',
  singleConfirm = false,
  onApplyPrimary,
  onApplySecondary,
  onSkip,
  onDone,
}) {
  const { t } = useI18n()
  const inline = variant === 'inline'
  const [selectedIdx, setSelectedIdx] = useState(singleConfirm ? 0 : null)
  const [stage, setStage] = useState('pick')          // 'pick' | 'cob'
  const [primaryIdx, setPrimaryIdx] = useState(null)
  const [busyIdx, setBusyIdx] = useState(null)
  // ★ INSURE-2 (P12): a secondary that did not save says so. Before, a failure only reset
  //   the spinner, and a "success" could be the app's own local object.
  const [cobError, setCobError] = useState(false)

  if (!Array.isArray(candidates) || candidates.length === 0) return null

  const cardPad = inline ? 12 : 14
  const cardRadius = inline ? 14 : 18
  const chipPx = inline ? 34 : 38

  const applyPrimary = async () => {
    const idx = singleConfirm ? 0 : selectedIdx
    if (idx === null || busyIdx !== null) return
    setBusyIdx(idx)
    try {
      const res = await onApplyPrimary?.(idx)
      if (res && res.success === false) { setBusyIdx(null); return }
      // COB: offer a second policy only when another *active* candidate WITH A MEMBER ID
      // exists (INSURE-2, D5: saving one without a member ID deleted the existing secondary).
      const otherActive = secondActiveIndex(candidates, idx) >= 0
      if (onApplySecondary && otherActive) {
        setPrimaryIdx(idx)
        setBusyIdx(null)
        setStage('cob')
        return
      }
      onDone?.(false)
    } catch (e) {
      console.warn('[InsuranceCandidateList] primary apply failed:', e?.message || e)
      setBusyIdx(null)
    }
  }

  const applySecondary = async (idx) => {
    if (busyIdx !== null) return
    setBusyIdx(idx)
    setCobError(false)
    try {
      const res = await onApplySecondary?.(idx)
      if (res && res.success === false) { setBusyIdx(null); setCobError(true); return }
      onDone?.(true)
    } catch (e) {
      console.warn('[InsuranceCandidateList] secondary apply failed:', e?.message || e)
      setBusyIdx(null)
      setCobError(true)
    }
  }

  // ── COB stage ──────────────────────────────────────────────────────────
  if (stage === 'cob') {
    const secIdx = secondActiveIndex(candidates, primaryIdx)
    const sec = secIdx >= 0 ? candidates[secIdx] : null
    return (
      <div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: inline ? 15 : 18, fontWeight: 800, letterSpacing: '-0.01em', color: NAVY }}>
            {t('insuranceCandidates.secondActivePlan', 'You have a second active plan')}
          </div>
          <div style={{ fontSize: 12.5, color: SLATE_INK, marginTop: 4, lineHeight: 1.5 }}>
            {t('insuranceCandidates.secondActivePlanBody', 'Add it as your secondary coverage so both can be billed correctly.')}
          </div>
        </div>
        {sec && (
          <div style={{ background: '#fff', border: `1.5px solid ${LIGHT}`, borderRadius: cardRadius,
                        padding: cardPad, marginBottom: 11, boxShadow: '0 1px 3px rgba(21,53,79,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
              {stateChip(sec, chipPx)}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', color: NAVY,
                              display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  {sec.payerName || t('onboarding.insurance.coverageGeneric', 'Coverage')}
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 9px', borderRadius: 999,
                                 background: 'rgba(70,168,249,0.14)', color: BLUE }}>{t('insuranceCandidates.addAsSecondBadge', 'Add as 2nd')}</span>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: SLATE_INK, marginTop: 3, letterSpacing: '0.02em' }}>
                  {memberLine(sec, t) || t('insuranceCandidates.statusActive', 'Active')}
                </div>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={() => applySecondary(secIdx)}
          disabled={busyIdx !== null}
          style={{ width: '100%', padding: 14, borderRadius: 999, border: 0, background: BLUE, color: '#fff',
                   fontSize: 14, fontWeight: 800, cursor: busyIdx !== null ? 'not-allowed' : 'pointer',
                   display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                   opacity: busyIdx !== null ? 0.7 : 1 }}>
          {busyIdx !== null ? (<><OrellaSpinner size={16} tone="inherit" /> {t('insuranceCandidates.adding', 'Adding…')}</>) : t('insuranceCandidates.addAsSecondary', 'Add as secondary')}
        </button>
        {cobError && (
          <div role="alert" style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, color: NAVY, lineHeight: 1.5,
                                     background: ICE, border: `1px solid ${LIGHT}`, borderRadius: 12, padding: '10px 12px' }}>
            {t('insuranceCandidates.secondaryNotSaved', "We couldn't add this plan. You can add it later under Profile → Insurance.")}
          </div>
        )}
        <button
          onClick={() => onDone?.(false)}
          style={{ width: '100%', padding: '11px 0 2px', background: 'none', border: 0, color: SLATE_INK,
                   fontSize: 12.5, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>
          {t('insuranceCandidates.justOnePlan', 'No, just the one plan')}
        </button>
      </div>
    )
  }

  // ── Pick stage ─────────────────────────────────────────────────────────
  return (
    <div>
      {candidates.map((cand, i) => {
        const selected = singleConfirm ? true : selectedIdx === i
        const pill = statusPill(cand, t)
        const cb = confBadge(cand.confidence, t)
        const sub = subscriberName(cand)
        const meta = []
        if (sub) meta.push([t('profileInsurance2.subscriber', 'Subscriber'), sub])
        meta.push([t('insuranceCandidates.metaConfidence', 'Confidence'), <span key="c" style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 999, textTransform: 'uppercase',
          letterSpacing: '0.04em', background: cb.bg, color: cb.fg }}>{cb.label}</span>])
        if (cand.groupNumber) meta.push([t('onboarding.insurance.group', 'Group'), cand.groupNumber])
        // ★ v242.149.0 — through the calendar owner; this printed the raw 271
        //   value (19840211) to a patient being asked to confirm their identity.
        if (cand.subscriberDob) meta.push([t('insuranceCandidates.metaDob', 'DOB on file'), formatDate(cand.subscriberDob) || cand.subscriberDob])

        return (
          <div
            key={`${cand.payerId || cand.payerName || 'cand'}-${i}`}
            onClick={() => { if (!singleConfirm) setSelectedIdx(i) }}
            style={{
              background: selected ? ICE : '#fff',
              border: `1.5px solid ${selected ? BLUE : '#EEF2F6'}`,
              borderRadius: cardRadius, padding: cardPad, marginBottom: inline ? 9 : 11,
              boxShadow: '0 1px 3px rgba(21,53,79,0.06)',
              cursor: singleConfirm ? 'default' : 'pointer', transition: 'all 0.15s',
            }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
              {stateChip(cand, chipPx)}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: inline ? 13.5 : 14.5, fontWeight: 800, letterSpacing: '-0.01em',
                              color: NAVY, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {cand.payerName || t('onboarding.insurance.coverageGeneric', 'Coverage')}
                  {pill && (
                    <span style={{ fontSize: 9.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                                   background: pill.bg, color: pill.fg, whiteSpace: 'nowrap' }}>{pill.text}</span>
                  )}
                </div>
                {memberLine(cand, t) && (
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: SLATE_INK, marginTop: 3, letterSpacing: '0.02em' }}>
                    {memberLine(cand, t)}
                  </div>
                )}
              </div>
              {!singleConfirm && (
                <span style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                               border: `2px solid ${selected ? BLUE : '#CBD9E6'}`,
                               background: selected ? BLUE : 'transparent',
                               display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  {selected && <Check size={11} strokeWidth={3.4} style={{ color: '#fff' }} />}
                </span>
              )}
            </div>
            {meta.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: inline ? 7 : 8, marginTop: inline ? 11 : 12 }}>
                {meta.map(([k, v], mi) => (
                  <div key={mi} style={{ background: selected ? '#fff' : ICE, borderRadius: inline ? 10 : 11, padding: inline ? '7px 9px' : '8px 10px' }}>
                    <div style={{ fontSize: 8.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 800, color: SLATE }}>{k}</div>
                    <div style={{ fontSize: inline ? 12.5 : 13, fontWeight: 800, color: NAVY, marginTop: 2, letterSpacing: '-0.01em' }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}

      {(() => {
        // v242.8.8: active-only rule — a plan can be used for billing ONLY when
        // eligibility confirms it active (active === true). Ended (false) and
        // unverified (null) are both blocked; the patient adds their current card
        // instead (eligibility re-verifies it downstream).
        const effIdx = singleConfirm ? 0 : selectedIdx
        const effCand = (effIdx !== null && effIdx !== undefined) ? candidates[effIdx] : null
        const blocked = !!effCand && effCand.active !== true
        if (blocked) {
          const ended = effCand.active === false
          return (
            <>
              <div style={{ background: ICE, border: `1px solid ${LIGHT}`, borderRadius: 12,
                            padding: '11px 13px', marginTop: 3, fontSize: 12.5, color: NAVY, lineHeight: 1.5 }}>
                <span style={{ fontWeight: 800 }}>
                  {ended
                    ? (effCand.terminationDate
                        ? t('insuranceCandidates.planEndedOn', { defaultValue: 'This plan has ended ({{date}}).', date: effCand.terminationDate })
                        : t('insuranceCandidates.planEndedNoDate', 'This plan has ended.'))
                    : t('insuranceCandidates.planUnconfirmed', 'We couldn’t confirm this plan is active.')}
                </span>{' '}
                {ended
                  ? t('insuranceCandidates.inactiveBilling', 'An inactive plan can’t be used for billing — add your current insurance card instead.')
                  : t('insuranceCandidates.activeOnlyBilling', 'Only an active plan can be used for billing — add your current insurance card instead.')}
              </div>
              {onSkip && (
                <button
                  onClick={onSkip}
                  style={{ width: '100%', padding: 14, borderRadius: 999, border: 0, marginTop: 10,
                           background: NAVY, color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                           display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Pencil size={14} /> {t('insuranceCandidates.addMyCurrentCard', 'Add my current card')}
                </button>
              )}
            </>
          )
        }
        return (
          <>
            <button
              onClick={applyPrimary}
              disabled={(!singleConfirm && selectedIdx === null) || busyIdx !== null}
              style={{
                width: '100%', padding: 14, borderRadius: 999, border: 0, marginTop: 3,
                background: (!singleConfirm && selectedIdx === null) ? '#CBD9E6' : (singleConfirm ? GREEN : BLUE),
                color: '#fff', fontSize: 14, fontWeight: 800,
                cursor: ((!singleConfirm && selectedIdx === null) || busyIdx !== null) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: busyIdx !== null ? 0.7 : 1,
              }}>
              {busyIdx !== null
                ? (<><OrellaSpinner size={16} tone="inherit" /> {t('insuranceCandidates.applying', 'Applying…')}</>)
                : (singleConfirm
                    ? (<><Check size={16} strokeWidth={2.6} /> {t('insuranceCandidates.confirmAndUseThisPlan', 'Confirm & use this plan')}</>)
                    : t('insuranceCandidates.useThisPlan', 'Use this plan'))}
            </button>

            {onSkip && (
              <button
                onClick={onSkip}
                style={{ width: '100%', padding: '11px 0 2px', background: 'none', border: 0, color: SLATE_INK,
                         fontSize: 12.5, fontWeight: 700, cursor: 'pointer', marginTop: 8,
                         display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Pencil size={13} /> {singleConfirm ? t('insuranceCandidates.notMine', "This isn't mine — add my card") : t('insuranceCandidates.noneOfThese', 'None of these — enter manually')}
              </button>
            )}
          </>
        )
      })()}
    </div>
  )
}
